/**
 * AetherLite Clients CRUD.
 *
 * Endpoints (todos requieren auth Bearer JWT):
 *   GET    /aetherlite/clients?status=&q=
 *   POST   /aetherlite/clients
 *   GET    /aetherlite/clients/:id
 *   PUT    /aetherlite/clients/:id
 *   POST   /aetherlite/clients/:id/archive
 *   POST   /aetherlite/clients/:id/restore
 *   DELETE /aetherlite/clients/:id
 *   GET    /aetherlite/clients/:id/plans
 *
 * Shapes mirror exactos de `aetherlite_clients.py` (32 campos en `ClientFull`).
 *
 * Errores especiales:
 *   - 402 al CREATE / RESTORE → tier cap excedido.
 *     `ApiError.detail` viene como
 *       { error: "limit_exceeded", message, current, limit, tier, suggested_tier }
 *     Los callers pueden hacer `e.status === 402 && e.detail?.error === 'limit_exceeded'`.
 */
import { apiFetch, ApiError } from "./client";

// ---------- Tipos enum (alineados con backend) ----------

export type ClientSex = "M" | "F";
export type ClientActivityLevel = "sedentary" | "light" | "moderate" | "intense" | "athlete";
export type ClientGoal = "maintain" | "deficit" | "surplus" | "recomp";
export type ClientLevel = "beginner" | "intermediate" | "advanced";
export type ClientStatusFilter = "active" | "archived" | "all";

// ---------- ClientFull (response shape de ClientDisplay del backend) ----------

export interface ClientFull {
  id: number;
  user_id: number;

  // Identidad
  full_name: string;
  email: string | null;
  phone: string | null;
  photo_url: string | null;

  // Antropométricos
  sex: ClientSex | null;
  birthdate: string | null; // YYYY-MM-DD
  height_cm: number | null;
  initial_weight_kg: number | null;
  current_weight_kg: number | null;
  waist_cm: number | null;
  neck_cm: number | null;
  hip_cm: number | null;

  // Fitness
  activity_level: ClientActivityLevel | null;
  goal: ClientGoal | null;
  level: ClientLevel | null;
  injuries: string | null;
  medical_notes: string | null;

  // Programa
  start_date: string | null;
  end_date: string | null;
  notes: string | null;

  // Status / meta
  is_active: boolean;
  plans_count: number;
  created_at: string;
  updated_at: string;
}

// ---------- Payloads ----------

/** POST /clients — sólo `full_name` es obligatorio. */
export interface ClientCreatePayload {
  full_name: string;
  email?: string | null;
  phone?: string | null;
  photo_url?: string | null;

  sex?: ClientSex | null;
  birthdate?: string | null;
  height_cm?: number | null;
  initial_weight_kg?: number | null;
  current_weight_kg?: number | null;
  waist_cm?: number | null;
  neck_cm?: number | null;
  hip_cm?: number | null;

  activity_level?: ClientActivityLevel | null;
  goal?: ClientGoal | null;
  level?: ClientLevel | null;
  injuries?: string | null;
  medical_notes?: string | null;

  start_date?: string | null;
  end_date?: string | null;
  notes?: string | null;
}

/** PUT /clients/:id — todos los campos opcionales (PATCH semántico). */
export type ClientUpdatePayload = Partial<ClientCreatePayload>;

/** Detalle de un plan vinculado al cliente. */
export interface ClientPlanItem {
  id: number;
  name: string;
  template: string;
  updated_at: string;
}

/** Detalle estructurado del 402 limit_exceeded del backend. */
export interface ClientLimitExceededDetail {
  error: "limit_exceeded";
  message: string;
  current: number;
  limit: number;
  tier: string;
  suggested_tier?: string | null;
}

/**
 * Lee `ApiError.detail` y devuelve el detalle estructurado si el error es
 * 402 limit_exceeded del cap de clientes. Devuelve null si no aplica.
 */
export function readLimitExceeded(err: unknown): ClientLimitExceededDetail | null {
  if (!(err instanceof ApiError) || err.status !== 402) return null;
  const raw = err.detail as { detail?: unknown } | undefined;
  // FastAPI envuelve detail en { detail: ... }
  const inner = (raw && typeof raw === "object" && "detail" in raw) ? raw.detail : raw;
  if (inner && typeof inner === "object" && (inner as ClientLimitExceededDetail).error === "limit_exceeded") {
    return inner as ClientLimitExceededDetail;
  }
  return null;
}

// ---------- Funciones ----------

interface ListClientsOpts {
  status?: ClientStatusFilter;
  q?: string;
}

export async function listClients(opts: ListClientsOpts = {}): Promise<ClientFull[]> {
  const params = new URLSearchParams();
  if (opts.status) params.set("status", opts.status);
  if (opts.q && opts.q.trim()) params.set("q", opts.q.trim());
  const qs = params.toString();
  return apiFetch<ClientFull[]>(`/aetherlite/clients${qs ? `?${qs}` : ""}`);
}

export async function getClient(id: number): Promise<ClientFull> {
  return apiFetch<ClientFull>(`/aetherlite/clients/${id}`);
}

/** Throws ApiError(402, ...) si el tier no permite más clientes. */
export async function createClient(payload: ClientCreatePayload): Promise<ClientFull> {
  return apiFetch<ClientFull>("/aetherlite/clients", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateClient(id: number, patch: ClientUpdatePayload): Promise<ClientFull> {
  return apiFetch<ClientFull>(`/aetherlite/clients/${id}`, {
    method: "PUT",
    body: JSON.stringify(patch),
  });
}

export async function archiveClient(id: number): Promise<ClientFull> {
  return apiFetch<ClientFull>(`/aetherlite/clients/${id}/archive`, { method: "POST" });
}

/** Throws ApiError(402, ...) si restaurar excede el cap del tier. */
export async function restoreClient(id: number): Promise<ClientFull> {
  return apiFetch<ClientFull>(`/aetherlite/clients/${id}/restore`, { method: "POST" });
}

export async function deleteClient(id: number): Promise<void> {
  return apiFetch<void>(`/aetherlite/clients/${id}`, { method: "DELETE" });
}

export async function listClientPlans(id: number): Promise<ClientPlanItem[]> {
  return apiFetch<ClientPlanItem[]>(`/aetherlite/clients/${id}/plans`);
}

// ---------- Magic link para app móvil del cliente ----------

export interface ClientMagicLink {
  token: string;
  /** Deep link `aetherlite://verify?token=...` — abre directo la app móvil. */
  deep_link: string;
  /** ISO datetime — cuándo deja de funcionar (~15 min después de generarlo). */
  expires_at: string;
  expires_in_minutes: number;
}

/** Genera un magic link para que el cliente entre a la app móvil sin email.
 *  El coach lo comparte por WhatsApp / SMS. */
export async function generateClientMagicLink(clientId: number): Promise<ClientMagicLink> {
  return apiFetch<ClientMagicLink>(`/aetherlite/clients/${clientId}/magic-link`, {
    method: "POST",
  });
}
